import { ReactNode, useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Calendar, ChevronDown, Info, Keyboard, Search } from "@digital-twins/design-system/icons";
import { SOCIAL_TOPIC_POSTS_SORTS, SocialTopicPostsSort } from "@digital-twins/shared-types";
import { Switch } from "@/components/ui/switch";
import { intlLocaleForUi } from "@/i18n";
import { cn } from "@/lib/utils";
import { localizedCountryName } from "@/lib/utils/localized-country-name";
import { IDLE_PILL_CLASS, SocialFilterToken } from "./SocialFilterPill";
import { SocialPlatformIcon } from "./SocialPlatformIcon";
import { SocialReasonTooltip } from "./SocialReasonTooltip";
import { SocialTopicSlotContext } from "./SocialTopicView";
import {
  SOCIAL_FACET_FILTER_KEY,
  SOCIAL_FILTER_MENU_FACETS,
  SocialFacetValueLabel,
  SocialFilterMenu,
  SocialFilterMenuFacet,
  SocialFilterPopover,
} from "./SocialFilterPopover";
import { SocialAuthorFilterPopover } from "./SocialAuthorFilterPopover";
import { socialTopicWindowDays } from "./useSocialTopicFilters";
import { registerTopicHotkey } from "./useTopicHotkeys";

/** The search waits this long after the last keystroke before it filters. */
export const SOCIAL_SEARCH_DEBOUNCE_MS = 250;

/** The sort menu's order: Most reach first (the default). */
const SORT_ORDER: Array<SocialTopicPostsSort> = ["views", "newest", "engagement"].filter(
  (sort): sort is SocialTopicPostsSort =>
    (SOCIAL_TOPIC_POSTS_SORTS as ReadonlyArray<string>).includes(sort),
);

type OpenMenu = SocialFilterMenuFacet | "author" | "sort" | null;

/** Width of the soft fade at a scroll edge of the pill strip. */
const STRIP_FADE_PX = 28;

const KBD_CLASS =
  "inline-flex h-5 min-w-5 items-center justify-center rounded border border-border px-1 text-xs font-normal text-muted-foreground";

/** A UTC day, `YYYY-MM-DD`, as "Oct 7" in the UI language. */
function formatTopicDay(day: string, uiLanguage: string): string {
  return new Intl.DateTimeFormat(intlLocaleForUi(uiLanguage), {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${day}T00:00:00.000Z`));
}

interface StripOverflow {
  hasStart: boolean;
  hasEnd: boolean;
}

/** Which edges of a horizontally scrollable element still hide content (RTL-safe via scrollLeft's sign). */
function readStripOverflow(element: HTMLElement | null): StripOverflow {
  if (!element) {
    return { hasStart: false, hasEnd: false };
  }
  const max = element.scrollWidth - element.clientWidth;
  const offset = Math.abs(element.scrollLeft);
  return { hasStart: offset > 1, hasEnd: max - offset > 1 };
}

/** A mask that fades only the edges that still hide content; none when everything fits. */
function stripMask(overflow: StripOverflow): string | undefined {
  if (!overflow.hasStart && !overflow.hasEnd) {
    return undefined;
  }
  const start = overflow.hasStart ? `transparent 0, #000 ${STRIP_FADE_PX}px` : "#000 0";
  const end = overflow.hasEnd ? `#000 calc(100% - ${STRIP_FADE_PX}px), transparent 100%` : "#000 100%";
  return `linear-gradient(to right, ${start}, ${end})`;
}

interface SocialFilterBarProps {
  /** Filters, sort and measure come from `context.topicFilters`. */
  context: SocialTopicSlotContext;
  /** Posts under the current filters (the `aria-live` count); null while loading. */
  postCount: number | null;
  onShowShortcuts: () => void;
}

/**
 * The sticky smart bar: search, facet pills, the date and author tokens,
 * sort, clear and the live post count.
 */
export function SocialFilterBar(props: SocialFilterBarProps) {
  const { context, postCount, onShowShortcuts } = props;

  const { universe, topicFilters } = context;
  const { filters, hasFilters, setFilter, clear } = topicFilters;
  const [draft, setDraft] = useState(filters.q);
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const stripRef = useRef<HTMLDivElement | null>(null);
  const [stripOverflow, setStripOverflow] = useState<StripOverflow>({ hasStart: false, hasEnd: false });

  const updateStripOverflow = useCallback(() => {
    const next = readStripOverflow(stripRef.current);
    setStripOverflow((current) =>
      current.hasStart === next.hasStart && current.hasEnd === next.hasEnd ? current : next,
    );
  }, []);

  const localFilterId = useId();
  const { t, i18n } = useTranslation("app");

  // The URL or a clear moved the search: follow it, unless a keystroke is pending.
  useEffect(() => {
    if (timerRef.current == null && filters.q !== draftRef.current) {
      setDraft(filters.q);
    }
  }, [filters.q]);

  useEffect(
    () => () => {
      if (timerRef.current != null) {
        clearTimeout(timerRef.current);
      }
    },
    [],
  );

  // The strip re-measures when its box or its pills change size, so the fades follow.
  useEffect(() => {
    const strip = stripRef.current;
    updateStripOverflow();
    if (!strip || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(updateStripOverflow);
    observer.observe(strip);
    Array.from(strip.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  });

  useEffect(
    () =>
      registerTopicHotkey("/", (event) => {
        event.preventDefault();
        searchRef.current?.focus();
      }),
    [],
  );

  const cancelPendingSearch = () => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const onSearchChange = (value: string) => {
    setDraft(value);
    cancelPendingSearch();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setFilter("q", value);
    }, SOCIAL_SEARCH_DEBOUNCE_MS);
  };

  const onSearchKeyDown = (key: string) => {
    if (key === "Enter") {
      cancelPendingSearch();
      setFilter("q", draftRef.current);
      return;
    }
    if (key === "Escape") {
      searchRef.current?.blur();
    }
  };

  const onClear = () => {
    cancelPendingSearch();
    setDraft("");
    setOpenMenu(null);
    clear();
  };

  const menuOpenChange = (menu: Exclude<OpenMenu, null>) => (isOpen: boolean) => {
    setOpenMenu((current) => (isOpen ? menu : current === menu ? null : current));
  };

  const isFacetShown = (facet: SocialFilterMenuFacet): boolean => {
    if (filters[SOCIAL_FACET_FILTER_KEY[facet]] != null) {
      return true;
    }
    if (facet === "channel") {
      return new Set(universe.channels.map((channel) => channel.platform)).size > 1;
    }
    if (facet === "brand") {
      return universe.brands.length > 0;
    }
    if (facet === "theme") {
      return universe.topics.length > 0;
    }
    return true;
  };

  const showsClear = hasFilters || draft.trim().length > 0;
  const days = socialTopicWindowDays(filters.window);
  const sortLabel = t(`socialListening.topicPage.filters.sortOptions.${filters.sort}`);

  const facetPills = SOCIAL_FILTER_MENU_FACETS.filter(isFacetShown).map((facet) => {
    const label = t(`socialListening.topicPage.filters.${facet}`);
    const value = filters[SOCIAL_FACET_FILTER_KEY[facet]];
    const testId = `topic-filter-${facet}`;
    const isOpen = openMenu === facet;
    const popover = (trigger: ReactNode) => (
      <SocialFilterPopover
        key={facet}
        facet={facet}
        context={context}
        isOpen={isOpen}
        onOpenChange={menuOpenChange(facet)}
        trigger={trigger}
      />
    );

    if (value == null) {
      return popover(
        <button type="button" aria-haspopup="listbox" className={IDLE_PILL_CLASS} data-testid={testId}>
          {label}
          <ChevronDown
            className="size-3.5 text-muted-foreground transition-transform duration-200 group-aria-expanded:rotate-180"
            aria-hidden="true"
          />
        </button>,
      );
    }
    return (
      <SocialFilterToken
        key={facet}
        value={null}
        removeLabel={t("socialListening.topicPage.filters.removeFilter", { label })}
        onRemove={() => {
          setOpenMenu(null);
          setFilter(SOCIAL_FACET_FILTER_KEY[facet], null);
        }}
        testId={testId}
      >
        {popover(
          <button
            type="button"
            aria-haspopup="listbox"
            className="inline-flex h-full items-center gap-1.5 rounded-l-full pl-3 focus-visible:outline-none"
            data-testid={testId}
          >
            <span className="font-normal text-muted-foreground">{label}</span>
            <span className="inline-flex min-w-0 max-w-48 items-center">
              <SocialFacetValueLabel facet={facet} value={String(value)} context={context} />
            </span>
          </button>,
        )}
      </SocialFilterToken>
    );
  });

  const dateToken = days ? (
    <SocialFilterToken
      value={
        <>
          <Calendar className="size-3.5 shrink-0" aria-hidden="true" />
          <span className="tabular-nums">
            {days.from === days.to
              ? formatTopicDay(days.from, i18n.language)
              : t("socialListening.topicPage.filters.dateRange", {
                  from: formatTopicDay(days.from, i18n.language),
                  to: formatTopicDay(days.to, i18n.language),
                })}
          </span>
        </>
      }
      removeLabel={t("socialListening.topicPage.filters.removeDate")}
      onRemove={() => setFilter("window", null)}
      testId="topic-filter-date"
    />
  ) : null;

  // A picked author lands at the strip's end: bring its chip, and so its ×, into view.
  const authorKey = filters.author ? `${filters.author.platform}:${filters.author.handle}` : null;
  useEffect(() => {
    if (!authorKey) {
      return;
    }
    const chip = stripRef.current?.querySelector<HTMLElement>('[data-testid="topic-filter-author-token"]');
    chip?.scrollIntoView?.({ block: "nearest", inline: "end" });
  }, [authorKey]);

  const authorLabel = t("socialListening.topicPage.filters.author");
  const authorPopover = (trigger: ReactNode) => (
    <SocialAuthorFilterPopover
      context={context}
      isOpen={openMenu === "author"}
      onOpenChange={menuOpenChange("author")}
      trigger={trigger}
    />
  );
  const authorChip = filters.author ? (
    <SocialFilterToken
      value={null}
      removeLabel={t("socialListening.topicPage.filters.removeAuthor")}
      onRemove={() => {
        setOpenMenu(null);
        setFilter("author", null);
      }}
      testId="topic-filter-author"
      // The strip scrolls sideways: the chip keeps its natural width so the × is never squeezed out of it.
      className="max-w-none"
    >
      {authorPopover(
        <button
          type="button"
          aria-haspopup="listbox"
          className="inline-flex h-full items-center gap-1.5 rounded-l-full pl-3 focus-visible:outline-none"
          data-testid="topic-filter-author"
        >
          <span className="font-normal text-muted-foreground">{authorLabel}</span>
          <span className="inline-flex min-w-0 max-w-48 items-center gap-1.5">
            <SocialPlatformIcon platform={filters.author.platform} className="size-3.5 shrink-0" />
            <span className="truncate">@{filters.author.handle}</span>
          </span>
        </button>,
      )}
    </SocialFilterToken>
  ) : (
    authorPopover(
      <button type="button" aria-haspopup="listbox" className={IDLE_PILL_CLASS} data-testid="topic-filter-author">
        {authorLabel}
        <ChevronDown
          className="size-3.5 text-muted-foreground transition-transform duration-200 group-aria-expanded:rotate-180"
          aria-hidden="true"
        />
      </button>,
    )
  );

  const sortMenu = (
    <SocialFilterMenu
      label={t("socialListening.topicPage.filters.sort")}
      options={SORT_ORDER.map((sort) => ({
        value: sort,
        label: <span>{t(`socialListening.topicPage.filters.sortOptions.${sort}`)}</span>,
        testId: `topic-sort-option-${sort}`,
      }))}
      selected={filters.sort}
      onSelect={(value) => {
        if (value) {
          setFilter("sort", value as SocialTopicPostsSort);
        }
      }}
      isOpen={openMenu === "sort"}
      onOpenChange={menuOpenChange("sort")}
      trigger={
        <button type="button" aria-haspopup="listbox" className={IDLE_PILL_CLASS} data-testid="topic-sort">
          <span className="font-normal text-muted-foreground">
            {t("socialListening.topicPage.filters.sort")}
          </span>
          {sortLabel}
          <ChevronDown
            className="size-3.5 text-muted-foreground transition-transform duration-200 group-aria-expanded:rotate-180"
            aria-hidden="true"
          />
        </button>
      }
      testId="topic-sort"
    />
  );

  const localFilterLabel = universe.marketCode
    ? t("socialListening.topicPage.filters.localFilter", {
        market: localizedCountryName(universe.marketCode, i18n.language),
      })
    : null;
  // Off is the server's default view, all markets, so the market stays out of the request; on is the only market view
  // a control reaches. It keeps its place whatever the counts say, so it never flickers as other filters change.
  const localFilter = universe.marketCode ? (
    <>
      <span aria-hidden="true" className="hidden h-5 w-px shrink-0 bg-border @[761px]:block" />
      <div
        className="absolute bottom-0 left-0 flex shrink-0 items-center gap-1.5 whitespace-nowrap pl-1 text-sm @[761px]:static"
        data-testid="topic-local-filter"
      >
        <Switch
          id={localFilterId}
          checked={filters.market === "inAndUnknown"}
          onCheckedChange={(checked) => setFilter("market", checked ? "inAndUnknown" : null)}
          data-testid="topic-local-filter-switch"
        />
        <label
          htmlFor={localFilterId}
          className="max-w-[220px] cursor-pointer truncate"
          title={localFilterLabel ?? undefined}
          data-testid="topic-local-filter-label"
        >
          {localFilterLabel}
        </label>
        <SocialReasonTooltip
          reason={t("socialListening.topicPage.filters.localFilterInfo", {
            market: localizedCountryName(universe.marketCode, i18n.language),
          })}
          isDisabled={false}
          isFocusable
          testId="topic-local-filter-info"
          className="items-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Info className="size-3.5" aria-hidden="true" data-testid="topic-local-filter-info" />
        </SocialReasonTooltip>
      </div>
    </>
  ) : null;

  return (
    <div
      className="@container sticky top-0 z-30 border-t border-[hsl(var(--card-border))] bg-background/85 py-2.5 backdrop-blur-md backdrop-saturate-150"
      data-testid="topic-filter-bar"
    >
      <div
        role="toolbar"
        aria-label={t("socialListening.topicPage.filters.ariaLabel")}
        className={cn(
          "relative flex min-h-10 flex-nowrap items-center gap-2",
          // Below 761px the market filter sits under the row, in this room, and the row keeps its one line.
          universe.marketCode && "pb-7 @[761px]:pb-0",
        )}
      >
        <label
          className={cn(
            "relative flex h-9 w-[120px] shrink-0 items-center rounded-full border border-input bg-background pl-[34px] pr-2.5",
            "transition-[border-color,box-shadow] duration-150 focus-within:border-ring focus-within:shadow-[0_0_0_3px_hsl(var(--ring)/0.18)]",
            "@[520px]:w-[220px] @[761px]:w-[300px]",
          )}
          data-testid="topic-search"
        >
          <Search className="absolute left-3 size-4 text-muted-foreground" aria-hidden="true" />
          <input
            ref={searchRef}
            type="search"
            value={draft}
            maxLength={200}
            autoComplete="off"
            placeholder={t("socialListening.topicPage.filters.search")}
            aria-label={t("socialListening.topicPage.filters.search")}
            className="w-full min-w-0 border-0 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            onChange={(event) => onSearchChange(event.target.value)}
            onKeyDown={(event) => onSearchKeyDown(event.key)}
            data-testid="topic-search-input"
          />
          <span className={KBD_CLASS} aria-hidden="true">
            /
          </span>
        </label>
        {/* One line, never wrapping: the pills and Sort scroll sideways between the fixed search and the fixed Clear and shortcuts. */}
        <div
          className="relative min-w-0 flex-1"
          style={{
            maskImage: stripMask(stripOverflow),
            WebkitMaskImage: stripMask(stripOverflow),
          }}
          data-testid="topic-filter-pills-mask"
          data-fade-start={stripOverflow.hasStart}
          data-fade-end={stripOverflow.hasEnd}
        >
          <div
            ref={stripRef}
            className="flex items-center gap-1.5 overflow-x-auto p-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            onScroll={updateStripOverflow}
            data-testid="topic-filter-pills"
          >
            {facetPills}
            {authorChip}
            {dateToken}
            <span aria-hidden="true" className="h-5 w-px shrink-0 bg-border" />
            {sortMenu}
          </div>
        </div>
        {/* Outside the strip, which scrolls, so it stays in view. */}
        {localFilter}
        <button
          type="button"
          tabIndex={showsClear ? 0 : -1}
          aria-hidden={!showsClear}
          className={cn(
            "inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full px-2.5 text-sm text-muted-foreground transition-opacity duration-200 hover:text-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            // Idle, it takes no room: a reserved slot pushed the sort out of the pill row at desktop widths.
            showsClear ? "opacity-100" : "pointer-events-none hidden opacity-0",
          )}
          onClick={onClear}
          data-testid="topic-filters-clear"
        >
          {t("socialListening.topicPage.filters.clear")}
        </button>
        <span aria-hidden="true" className="hidden h-5 w-px shrink-0 bg-border @[761px]:block" />
        <span
          aria-live="polite"
          // Announced to screen readers only: the stats card shows the count (one count on the page).
          className="sr-only"
          data-testid="topic-post-count"
        >
          {postCount != null
            ? t("socialListening.topicPage.filters.count", { count: postCount })
            : null}
        </span>
        <button
          type="button"
          aria-label={t("socialListening.topicPage.filters.shortcuts")}
          title={t("socialListening.topicPage.filters.shortcuts")}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={onShowShortcuts}
          data-testid="topic-shortcuts"
        >
          <Keyboard className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
